import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";

import { useTranslation } from "react-i18next";
import TopbarSidebarButton from "./TopbarSidebarButton";
import TopbarProfile from "./TopbarProfile";
import TopbarLanguage from "./TopbarLanguage";
import { routes } from "../../App/Router";
import LogoController from '../../../components/logos/logoController';
import { useSelector } from "react-redux";
import { isUserCitySelector, isUserOrganisationSelector, isUserTenantSelector } from "redux/selectors/user";
import sharedHooks from "hooks/shared.hooks";
import { Button } from "reactstrap";
import cityHooks from "hooks/city.hooks";
import usersHooks from "hooks/users.hooks";
import impersonationHooks from "hooks/impersonation.hooks";

const project = import.meta.env.VITE_FIREBASE_PROJECT_ID;

export default function Topbar({
  changeMobileSidebarVisibility,
  changeSidebarVisibility,
  topbarContent,
}) {
  const isTenant = useSelector(isUserTenantSelector);
  const isCity = useSelector(isUserCitySelector);
  const isOrganization = useSelector(isUserOrganisationSelector);
  const { details } = usersHooks.useExternalUser();
  const {
    enabled: impersonationEnabled,
    canAccessImpersonation,
    isImpersonating,
    impersonationState,
    canStartImpersonation,
    start,
    stop,
  } = impersonationHooks.useImpersonation();

  const [t] = useTranslation("common");
  const { limitSettings } = cityHooks.useFetchCityLimitSettings(details?.cityId);

  const { isEnglishAvailable } = sharedHooks.useIsEnglishAvailable();

  const logoLink = useMemo(() => {
    if (isTenant) {
      return routes.tenant.dashboard;
    }
    if (isCity) {
      return routes.city.dashboard;
    }
    if (isOrganization) {
      return routes.organisation.dashboard;
    }
    // default
    return routes.organisation.dashboard;
  }, [isCity, isOrganization, isTenant]);

  const disabledButton = !((project === "defisansautosolo-17ee7" || project === "greenplay-test-d85fa") && isOrganization);

  const handleStartImpersonation = async () => {
    const email = window.prompt(t("admin.impersonation_email_prompt"), "");

    if (typeof email !== "string") {
      return;
    }

    const normalizedEmail = email.trim();

    if (!normalizedEmail) {
      return;
    }

    const reason = window.prompt(t("admin.impersonation_reason_prompt"), "") || undefined;
    await start({ email: normalizedEmail, reason });
  };

  const handleStopImpersonation = async () => {
    await stop();
  };

  return (

    <div className="topbar" >
      <div className="topbar__wrapper">
        <div className="topbar__left">
          <TopbarSidebarButton
            changeMobileSidebarVisibility={changeMobileSidebarVisibility}
            changeSidebarVisibility={changeSidebarVisibility}
          />

          <Link to={logoLink} className="dashboard-logo-container">
            <LogoController isDashboard={true} />
          </Link>
        </div>
        <div style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center", gap: 12 }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, width: "100%" }}>
            {topbarContent}
            {impersonationEnabled && isImpersonating ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 12,
                  flexWrap: "wrap",
                  padding: "8px 12px",
                  background: "#fff4cc",
                  border: "1px solid #f0d36b",
                  borderRadius: 8,
                }}
              >
                <span style={{ fontWeight: 600 }}>
                  {t("admin.impersonation_active", {
                    email: impersonationState?.impersonatedEmail || impersonationState?.impersonatedUid || t("global.unknown"),
                  })}
                </span>
                <span>
                  {t("admin.impersonation_by", {
                    email: impersonationState?.impersonatorEmail || t("global.unknown"),
                  })}
                </span>
                <Button color="warning" size="sm" onClick={handleStopImpersonation}>
                  {t("admin.stop_impersonation")}
                </Button>
              </div>
            ) : null}
          </div>
        </div>

        <div className="topbar__right">
          <TopbarProfile />
          {limitSettings?.c25_challenge_between_organization?.granted &&
            isOrganization && (
              <Link
                to={disabledButton ? undefined : routes.organisation.challengeCreateForAnotherOrganisation}
                className="topbar__challenge-link"
              >
                <Button
                  color="primary"
                  className="topbar__challenge-button"
                  disabled={disabledButton}
                >
                  {t("global.send_challenge")}
                </Button>
              </Link>
            )}
          {isEnglishAvailable && <TopbarLanguage />}
        </div>
      </div>

    </div>


  );
}

Topbar.propTypes = {
  changeMobileSidebarVisibility: PropTypes.func.isRequired,
  changeSidebarVisibility: PropTypes.func.isRequired,
  topbarContent: PropTypes.node,
};
